import { AlertTriangle, Loader2, RotateCcw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { useClearChannelDeliveryBlocks, useWhatsAppBillingBlocks } from "@/hooks/useWhatsAppBillingBlocks";
import { PAYMENT_HELP_URL } from "@/components/MetaServiceBillingBanner";
import {
  billingBlockCopy,
  canClearChannelBlock,
  summarizeBillingBlocks,
  type WhatsAppBillingBlock,
} from "@/lib/whatsapp-billing-block";

export interface WhatsAppBillingBlockBannerProps {
  blocks: WhatsAppBillingBlock[];
  isAdmin: boolean;
  onRetry?: () => void;
  retrying?: boolean;
}

export function WhatsAppBillingBlockBanner({ blocks, isAdmin, onRetry, retrying = false }: WhatsAppBillingBlockBannerProps) {
  const summary = summarizeBillingBlocks(blocks);
  if (!summary) return null;
  const copy = billingBlockCopy(summary, { isAdmin });

  return (
    <div role="alert" className="shadow-panel mx-3 mt-3 rounded-xl bg-destructive px-4 py-3 text-destructive-foreground">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm">
            <strong>{copy.headline}</strong> {copy.cause} <strong>{copy.impact}</strong> {copy.action}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={PAYMENT_HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-background text-foreground hover:bg-background/90 px-4 py-1.5 rounded-md text-sm font-semibold whitespace-nowrap"
          >
            Cómo actualizar el método de pago
          </a>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              disabled={retrying}
              title="Si ya actualizaste el pago en Meta, el asistente vuelve a responder desde el próximo mensaje."
              className="inline-flex items-center gap-1.5 border border-destructive-foreground/60 hover:bg-destructive-foreground/10 px-4 py-1.5 rounded-md text-sm font-semibold whitespace-nowrap disabled:opacity-60"
            >
              {retrying
                ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <RotateCcw className="h-4 w-4" aria-hidden="true" />}
              Ya lo resolví, reintentar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function WhatsAppBillingBanners() {
  const { activeWorkspaceId, userRole } = useAuth();
  const { isSuperAdmin } = useSuperAdmin();
  const blocks = useWhatsAppBillingBlocks(activeWorkspaceId);
  const clearBlocks = useClearChannelDeliveryBlocks(activeWorkspaceId);

  if (blocks.length > 0) {
    const canRetry = canClearChannelBlock(userRole, isSuperAdmin);
    return (
      <WhatsAppBillingBlockBanner
        blocks={blocks}
        isAdmin={userRole === "admin" || isSuperAdmin}
        onRetry={canRetry ? () => clearBlocks.mutate(blocks.map((b) => b.channelId)) : undefined}
        retrying={clearBlocks.isPending}
      />
    );
  }
  return null;
}
